import * as React from 'react';
import { Link } from 'react-router';
import { useAppSelector as useSelector } from 'Store/index';
import { without } from 'ramda';
import DashboardGraph from 'Components/DashboardGraph';
import ContentWrapper from 'Components/ContentWrapper';
import Tile from 'Components/DashboardTile';
import { Container, Item } from 'Components/Grid';
import ErrorContainer from 'Containers/ErrorContainer';

import { styleDash } from '../../config/theme/componentTheme';
import { filteredDocumentTypes } from '../../config/generalSettings';

import { getEnabledDocuments, getRelevantDocuments } from 'Utility/randomUtil';
import { isEnded } from 'Utility/isEnded';
import { filterByDocumentType, filterDocumentsByType } from 'Utility/documentHandling';

import Icon from './Icon';
import MyDash from 'Routes/MyService/Dash';

import { getActiveTreatments, getRightDocumentsForPlatform } from '../Treatment/utils/index';
import type { RelevantDocumentType as TRelevantTreatmentDocumentType } from '../Treatment/Document/definitions';
import { withCapabilities } from 'Containers/CapabilityHandler';
import { checkMyServCapability, isMyServiceAvailable } from 'Routes/MyService/util';
import { capitalize } from 'Utility/string';
import { ninmtFileTypes } from 'Routes/PatientFiles/utils';

// Find if there are any documents being edited
const editingDocuments = (documents: IDocumentV2[], section: string, session?: ISessionStore): boolean => {
  const docTypes = session ? getEnabledDocuments(session) : [];
  const thisDocTypes = docTypes.find(t => t.name === section);

  // Filter specific documents based on filtering function
  const subTypeFilter = (singleDoc: IDocumentV2): boolean => {
    const type = singleDoc._type;
    if (thisDocTypes?.subTypesFiltering?.[type]) {
      const f = thisDocTypes.subTypesFiltering[type];
      // return thisDocTypes.subTypesFiltering[type](singleDoc);
      return f(singleDoc as any);
    }
    return true;
  };

  // True, if editing document is of the same type as the tile, or if it's included in the subTypes
  return documents.some(
    d => d._editing && (d._type === section || (thisDocTypes?.subTypes ?? []).includes(d._type)) && subTypeFilter(d),
  );
};

// Find if there are no documents
// NOTE: No documents message is only shown if there is NO documents (this does not include ended medication/treatments/etc)
const noDocs = (documents: IDocumentV2[], n: string, session?: ISessionStore, myappDocs?: IDocumentV2[]): boolean => {
  if (!session) return false;

  const docTypes = getEnabledDocuments(session);
  const thisDocTypes = docTypes.find(t => t.name === n);
  const selectedPlatform = session.platforms?.selected;

  const shouldCheckMyAppDocs =
    Array.isArray(myappDocs) &&
    myappDocs.length > 0 &&
    ((selectedPlatform === 'epilepsy' && n === 'seizure') ||
      ((selectedPlatform === 'sleepApnea' || selectedPlatform === 'mgravis') && n === 'background') ||
      (selectedPlatform === 'parkinson' && (n === 'background' || n === 'lifestyle' || n === 'diagnosticSurvey')));

  // Check if any documents match the main or subType
  const hasDocument = documents.some(d => d._type === n || (thisDocTypes?.subTypes ?? []).includes(d._type));
  let hasMyAppDocument = false;

  if (shouldCheckMyAppDocs) {
    hasMyAppDocument = myappDocs.some(d => {
      switch (n) {
        case 'seizure':
          return d._type === 'patientReportedSeizure';
        case 'background':
          return d._type.toLowerCase().includes('background');
        case 'lifestyle':
          return d._type === 'parkinsonLifestyle';
        case 'diagnosticSurvey':
          return d._type === 'iadl' || d._type === 'fbimod';
        default:
          return false;
      }
    });
  }

  // Return true if no documents match the main or subType and there are no relevant my app documents
  return !hasDocument && !hasMyAppDocument;
};

const getDocumentsLength = (
  platform: Platform | undefined,
  documentType: string,
  documents: IDocumentV2[],
): number | null => {
  if (documents.length < 1) return null;
  if (!platform) return null;
  switch (platform) {
    case 'sma':
    case 'dmd': {
      if (documentType === 'therapy') {
        const activeTherapies = filterByDocumentType(documents, 'therapy').filter(t => !isEnded(t.endDate));
        return activeTherapies.length;
      }
      return null;
    }
    case 'ms': {
      if (documentType === 'therapy') {
        const activeTherapies = filterByDocumentType(documents, 'therapy').filter(t => !isEnded(t.endDate));
        return activeTherapies.length;
      }
      if (documentType === 'comorbidity') {
        const active = filterByDocumentType(documents, 'comorbidity').filter(t => !isEnded(t.endDate));
        return active.length;
      }
      return null;
    }
    case 'epilepsy': {
      if (documentType === 'surgicalTreatment') {
        return documents.length;
      }
      if (documentType === 'otherSymptoms') {
        const activeDocs = filterByDocumentType(documents, 'otherSymptoms').filter(t => !isEnded(t.endDate));
        return activeDocs.length;
      }
      return null;
    }
    case 'sleepApnea': {
      if (documentType === 'diagnosis') {
        // Only .length is used below, so the docs never need narrowing past this shared field check.
        const sleepApneaAndRespiratoryFailureDocs = filterDocumentsByType(documents, [
          'diagnosis',
          'sleepApneaFirstVisit',
          'respiratoryFirstVisit',
        ]).filter(
          d =>
            'diagnosis' in d &&
            ['G47.3', 'J96.1', 'J96.9', 'sleepApneaSuspicion', 'respiratoryFailureSuspicion'].includes(
              d.diagnosis ?? '',
            ),
        );
        return sleepApneaAndRespiratoryFailureDocs.length;
      }
      if (documentType === 'treatment') {
        // Narrow to what getRightDocumentsForPlatform actually declares (RelevantDocumentType), so no
        // cast is needed at the call below.
        const docs = filterDocumentsByType(documents, [
          'patientDoesNotWantRespiratorySupportTherapy',
          'oxygenTherapy',
          'hfncTherapy',
          'papTherapy',
          'madTherapy',
          'sleepApneaSurgicalTreatment',
          'otherTreatment',
          'diagnosis',
        ] satisfies TRelevantTreatmentDocumentType[]);
        const treatmentDocsFiltered = getRightDocumentsForPlatform(docs);
        const activeDocs = getActiveTreatments(treatmentDocsFiltered);
        return activeDocs.length;
      }
      return null;
    }
    default:
      return null;
  }
};

const TileContent = ({ section, documents }: { section: string; documents: IDocumentV2[] }): React.JSX.Element => {
  // oxlint-disable-next-line typescript/no-require-imports
  return React.createElement(require('../' + capitalize(section) + '/Dash/').default, { documents });
};

const fileTypesPerPlatform = (platform?: string) => ({ ninmt: ninmtFileTypes })[platform ?? ''];

const DEFAULT_CAPABILITY_GROUPS: IGroupACLMapping = {};
const RenderTile = ({
  section,
  session,
  capabilityGroups = DEFAULT_CAPABILITY_GROUPS,
}: {
  section: string;
  session?: ISessionStore;
  capabilityGroups?: IGroupACLMapping;
}): React.JSX.Element => {
  const platform = session?.platforms?.selected;

  const docs = useSelector(s => s.documents.sortedAndMergedDocuments)?.slice() ?? [];
  const myappDocs = useSelector(s => s.myapp.sortedAndMergedDocuments);
  const files = useSelector(s => s.documents.files)?.slice() ?? [];

  const filesForPlatform = files.filter(f => (fileTypesPerPlatform(platform) ?? []).includes(f.subType));

  const enabledName = useSelector(state => getEnabledDocuments(state.session).find(d => d.name === section));
  const relevantDocs = getRelevantDocuments(docs, enabledName, section);

  /** Always show the tile contents for these sections */
  const sectionsToAlwaysShowContent = ['profile'];
  if (platform === 'parkinson') {
    sectionsToAlwaysShowContent.push('updrs');
    sectionsToAlwaysShowContent.push('cognition');
  }

  return (
    <Item key={section} xs="auto">
      {section === 'myService' &&
      platform &&
      !isMyServiceAvailable(platform, checkMyServCapability(platform, capabilityGroups), docs) ? (
        <ErrorContainer>
          {checkMyServCapability(platform, capabilityGroups) ? <MyDash isClickable={false} /> : <></>}
        </ErrorContainer>
      ) : (
        <Link to={'/' + section}>
          <ErrorContainer errorClickToFrontpage={false}>
            {section === 'myService' ? ( // Get custom tile for My Service
              platform && checkMyServCapability(platform, capabilityGroups) ? (
                <MyDash isClickable={true} />
              ) : (
                <></>
              )
            ) : section === 'patientFiles' ? (
              <Tile
                section={section}
                content={<TileContent section={section} documents={files} />}
                editing={false}
                noDocs={filesForPlatform.length === 0}
                icon={<Icon name={section} />}
                platform={platform}
              />
            ) : (
              <Tile
                section={section}
                content={<TileContent section={section} documents={relevantDocs} />}
                editing={editingDocuments(docs, section, session)}
                noDocs={
                  sectionsToAlwaysShowContent.includes(section) ? false : noDocs(docs, section, session, myappDocs)
                }
                icon={<Icon name={section} />}
                platform={platform}
                documentsLength={getDocumentsLength(platform, section, relevantDocs)}
              />
            )}
          </ErrorContainer>
        </Link>
      )}
    </Item>
  );
};

const TileWithCapabilities = withCapabilities(RenderTile);

const Content = ({
  sections,
  filteredSections,
  session,
}: {
  sections: string[];
  filteredSections: string[];
  session: ISessionStore;
}): React.JSX.Element => (
  <ContentWrapper>
    <Container justifyContent="center">
      <Item>
        <Container style={{ ...styleDash }} rowSpacing={'0rem'}>
          {without(filteredSections, sections).map((section: string) => (
            <TileWithCapabilities key={section} section={section} session={session} />
          ))}
        </Container>
      </Item>
    </Container>
  </ContentWrapper>
);

const Dashboard = ({ sections }: IOwnProps): React.JSX.Element => {
  const session = useSelector((s: IState) => s.session);
  const platform = session.platforms?.selected;
  const documents = useSelector((s: IState) => s.documents.sortedAndMergedDocuments);
  const filteredSections = filteredDocumentTypes(platform, documents);

  return (
    <>
      {platform === 'polyneuro' ? (
        <></>
      ) : (
        <ErrorContainer>
          <React.StrictMode>
            <DashboardGraph />
          </React.StrictMode>
        </ErrorContainer>
      )}
      <Content sections={sections} filteredSections={filteredSections} session={session} />
    </>
  );
};

interface IOwnProps {
  sections: string[];
}

export default Dashboard;
